import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Users, ClipboardCheck, Eye, FileText, ArrowRight, TrendingUp,
  ClipboardList, CheckCircle2, XCircle, ShieldAlert, Flame,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, Badge, LoadingState, Table, THead, TBody, TR, TH, TD, EmptyState } from '../../components/ui';

// Validated categorical + status palette (see the dataviz skill's
// references/palette.md) -- fixed hue-per-entity assignments below, never
// re-cycled by rank, so a given exam status / role / violation type always
// reads as the same color.
const CATEGORICAL = {
  blue: '#2a78d6', orange: '#eb6834', aqua: '#1baf7a', yellow: '#eda100',
  magenta: '#e87ba4', green: '#008300', violet: '#6250d6', red: '#e34948',
};
const STATUS = { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b' };

const EXAM_STATUS_COLORS = { DRAFT: CATEGORICAL.blue, ACTIVE: CATEGORICAL.aqua, COMPLETED: CATEGORICAL.violet };
const VIOLATION_COLORS = {
  TAB_SWITCH: CATEGORICAL.blue, FULLSCREEN_EXIT: CATEGORICAL.orange, RIGHT_CLICK: CATEGORICAL.aqua,
  COPY_PASTE: CATEGORICAL.yellow, KEYBOARD_SHORTCUT: CATEGORICAL.magenta, FOCUS_LOSS: CATEGORICAL.green,
  DISCONNECT: CATEGORICAL.violet, DEVTOOLS_OPEN: CATEGORICAL.red, NO_FACE_DETECTED: CATEGORICAL.blue,
  MULTIPLE_FACES_DETECTED: CATEGORICAL.orange, OTHER: '#898781',
};

const humanize = (s) => s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const formatDay = (isoDate) =>
  new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card padding="p-4">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${color}`}>
      <Icon className="w-[18px] h-[18px]" />
    </div>
    <p className="text-2xl font-bold text-slate-800">{value ?? '—'}</p>
    <p className="text-sm font-medium text-slate-600 mt-1 truncate">{title}</p>
    {subtitle && <p className="text-xs text-slate-400 mt-0.5 truncate">{subtitle}</p>}
  </Card>
);

// Single-series line/area chart for "attempts over time". A single series
// needs no legend (the card title names it); hover shows a per-point tooltip
// since an SVG chart is interactive by default.
const AttemptsLineChart = ({ data, color = CATEGORICAL.blue }) => {
  const [hover, setHover] = useState(null);
  const width = 600, height = 160;
  const padding = { top: 16, right: 10, bottom: 8, left: 10 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const maxVal = Math.max(1, ...data.map((d) => d.count));
  const stepX = innerW / Math.max(1, data.length - 1);

  const points = data.map((d, i) => ({
    ...d,
    x: padding.left + i * stepX,
    y: padding.top + innerH - (d.count / maxVal) * innerH,
  }));
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const baseY = padding.top + innerH;
  const areaPath = `${linePath} L ${points[points.length - 1].x.toFixed(1)} ${baseY} L ${points[0].x.toFixed(1)} ${baseY} Z`;

  return (
    <div className="relative attempts-line-chart">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height, overflow: 'visible' }}>
        <line x1={padding.left} y1={baseY} x2={width - padding.right} y2={baseY} stroke="#e1e0d9" strokeWidth="1" />
        <path d={areaPath} fill={color} fillOpacity="0.08" stroke="none" />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={p.date} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }}>
            <circle cx={p.x} cy={p.y} r="10" fill="transparent" />
            <circle cx={p.x} cy={p.y} r={hover === i ? 4 : 2.5} fill={color} stroke="#fff" strokeWidth="1.5" />
          </g>
        ))}
      </svg>
      {hover !== null && (
        <div
          className="absolute bg-slate-900 text-white text-xs rounded-md px-2.5 py-1.5 pointer-events-none shadow-lg whitespace-nowrap z-10"
          style={{
            left: `${(points[hover].x / width) * 100}%`,
            top: `${(points[hover].y / height) * 100}%`,
            transform: 'translate(-50%, -130%)',
          }}
        >
          <div className="font-semibold">{points[hover].count} attempt{points[hover].count !== 1 ? 's' : ''}</div>
          <div className="text-slate-300">{formatDay(points[hover].date)}</div>
        </div>
      )}
      <div className="flex justify-between text-[11px] text-slate-400 mt-1">
        <span>{formatDay(data[0]?.date)}</span>
        <span>{formatDay(data[Math.floor((data.length - 1) / 2)]?.date)}</span>
        <span>{formatDay(data[data.length - 1]?.date)}</span>
      </div>
    </div>
  );
};

// Reusable horizontal bar list for categorical breakdowns (exam status, user
// roles, violation types, most-attempted exams) -- color is always paired
// with a direct text label and value, never carrying meaning alone.
const BarList = ({ items }) => {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.label}>
          <div className="flex items-center justify-between text-sm mb-1 gap-2">
            <span className="flex items-center gap-2 text-slate-700 font-medium truncate min-w-0">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="truncate">{item.label}</span>
            </span>
            <span className="text-slate-500 font-semibold flex-shrink-0">{item.value}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(item.value / max) * 100}%`, backgroundColor: item.color }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Pass/fail split as a single stacked proportion bar -- a true status
// encoding (not identity), so it uses the reserved status colors rather than
// the categorical palette.
const PassFailBar = ({ passed, failed }) => {
  const total = passed + failed;
  if (total === 0) {
    return <EmptyState icon={CheckCircle2} title="No pass/fail data yet" description="Set a passing mark on an exam to see its results here." />;
  }
  const passPct = Math.round((passed / total) * 100);
  return (
    <div>
      <div className="h-4 rounded-full overflow-hidden flex bg-slate-100">
        <div style={{ width: `${passPct}%`, backgroundColor: STATUS.good }} />
        <div style={{ width: `${100 - passPct}%`, backgroundColor: STATUS.critical }} />
      </div>
      <div className="flex items-center justify-between mt-4 text-sm">
        <span className="flex items-center gap-1.5 text-slate-700">
          <CheckCircle2 className="w-4 h-4" style={{ color: STATUS.good }} />
          Passed <b className="text-slate-900">{passed}</b> <span className="text-slate-400">({passPct}%)</span>
        </span>
        <span className="flex items-center gap-1.5 text-slate-700">
          <XCircle className="w-4 h-4" style={{ color: STATUS.critical }} />
          Failed <b className="text-slate-900">{failed}</b> <span className="text-slate-400">({100 - passPct}%)</span>
        </span>
      </div>
    </div>
  );
};

const ChartCard = ({ icon: Icon, title, subtitle, children }) => (
  <Card>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-slate-400" />
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
    </div>
    {subtitle && <p className="text-xs text-slate-400 mb-4">{subtitle}</p>}
    <div className={subtitle ? '' : 'mt-4'}>{children}</div>
  </Card>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentExams, setRecentExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, examsRes] = await Promise.all([
          api.get('/admin/stats').catch(() => ({ data: {} })),
          api.get('/exams?limit=5').catch(() => ({ data: { exams: [] } }))
        ]);
        setStats(statsRes.data?.data || statsRes.data || {});
        setRecentExams(examsRes.data?.data?.exams || []);
      } catch (err) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <LoadingState label="Loading dashboard..." />;
  }

  const examStatusItems = [
    { label: 'Draft', value: stats?.draftExams ?? 0, color: EXAM_STATUS_COLORS.DRAFT },
    { label: 'Active', value: stats?.activeExams ?? 0, color: EXAM_STATUS_COLORS.ACTIVE },
    { label: 'Completed', value: stats?.completedExams ?? 0, color: EXAM_STATUS_COLORS.COMPLETED },
  ];

  const violationItems = (stats?.violationsByType || []).map((v) => ({
    label: humanize(v.type),
    value: v.count,
    color: VIOLATION_COLORS[v.type] || '#898781',
  }));

  const topExamItems = (stats?.topExams || []).map((e) => ({
    label: e.name,
    value: e.attempts,
    color: CATEGORICAL.blue,
  }));

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
          <p className="text-slate-500 text-sm mt-1">Overview of your examination system</p>
        </div>
        <Link
          to="/admin/exams"
          className="inline-flex items-center gap-2 bg-themePrimary hover:brightness-110 hover:shadow-md text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-all active:scale-[0.98]"
        >
          <Plus className="w-4 h-4" />
          Create Exam
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Students"
          value={stats?.totalStudents ?? 0}
          subtitle="Registered students"
          color="bg-blue-100 text-blue-600"
          icon={Users}
        />
        <StatCard
          title="Active Exams"
          value={stats?.activeExams ?? 0}
          subtitle="Currently running"
          color="bg-green-100 text-green-600"
          icon={ClipboardCheck}
        />
        <StatCard
          title="Total Proctors"
          value={stats?.totalProctors ?? 0}
          subtitle="Available proctors"
          color="bg-purple-100 text-purple-600"
          icon={Eye}
        />
        <StatCard
          title="Total Exams"
          value={stats?.totalExams ?? 0}
          subtitle={`Draft: ${stats?.draftExams ?? 0} · Completed: ${stats?.completedExams ?? 0}`}
          color="bg-yellow-100 text-yellow-600"
          icon={FileText}
        />
        <StatCard
          title="Submitted Attempts"
          value={stats?.totalSubmittedAttempts ?? 0}
          subtitle="All-time submissions"
          color="bg-teal-100 text-teal-600"
          icon={ClipboardList}
        />
        <StatCard
          title="Average Score"
          value={stats?.averageScorePercentage != null ? `${stats.averageScorePercentage}%` : '—'}
          subtitle="Across all submitted attempts"
          color="bg-indigo-100 text-indigo-600"
          icon={TrendingUp}
        />
      </div>

      {/* Chart row 1: attempts over time, pass/fail, exam status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard icon={TrendingUp} title="Submitted Attempts" subtitle="Last 14 days">
          <AttemptsLineChart data={stats?.attemptsPerDay || []} />
        </ChartCard>
        <ChartCard icon={CheckCircle2} title="Pass / Fail" subtitle="Submitted attempts on exams with a passing mark set">
          <PassFailBar passed={stats?.passFail?.passed ?? 0} failed={stats?.passFail?.failed ?? 0} />
        </ChartCard>
        <ChartCard icon={FileText} title="Exams by Status">
          <BarList items={examStatusItems} />
        </ChartCard>
      </div>

      {/* Chart row 2: violations, top exams, user roles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard icon={ShieldAlert} title="Proctoring Violations" subtitle="All-time, by type">
          {violationItems.length === 0 ? (
            <EmptyState icon={ShieldAlert} title="No violations recorded" />
          ) : (
            <BarList items={violationItems} />
          )}
        </ChartCard>
        <ChartCard icon={Flame} title="Most Active Exams" subtitle="By submitted attempts">
          {topExamItems.length === 0 ? (
            <EmptyState icon={Flame} title="No submitted attempts yet" />
          ) : (
            <BarList items={topExamItems} />
          )}
        </ChartCard>
        <ChartCard icon={Users} title="Users by Role">
          <BarList items={[
            { label: 'Students', value: stats?.totalStudents ?? 0, color: CATEGORICAL.blue },
            { label: 'Proctors', value: stats?.totalProctors ?? 0, color: CATEGORICAL.magenta },
            { label: 'Admins', value: stats?.totalAdmins ?? 0, color: CATEGORICAL.violet },
          ]} />
        </ChartCard>
      </div>

      {/* Recent Exams Table */}
      <Card padding="p-0">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-800">Recent Exams</h3>
          <Link to="/admin/exams" className="inline-flex items-center gap-1 text-sm text-themePrimary hover:brightness-110 font-medium">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {recentExams.length === 0 ? (
          <EmptyState icon={FileText} title="No exams yet" description="Create your first exam to get started." />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Exam Title</TH>
                <TH>Subject</TH>
                <TH>Duration</TH>
                <TH>Status</TH>
                <TH>Students</TH>
                <TH>Action</TH>
              </TR>
            </THead>
            <TBody>
              {recentExams.map((exam) => (
                <TR key={exam._id || exam.id}>
                  <TD className="font-medium text-slate-900">{exam.name}</TD>
                  <TD>{exam.subject || '—'}</TD>
                  <TD>{exam.duration} min</TD>
                  <TD><Badge>{exam.status}</Badge></TD>
                  <TD>{exam.studentCount ?? exam.students?.length ?? 0}</TD>
                  <TD>
                    <Link to={`/admin/exams/${exam._id || exam.id}`} className="text-themePrimary hover:brightness-110 font-medium">
                      View
                    </Link>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default AdminDashboard;
